import HorizontalField from "../HorizontalField/HorizontalField"
import React from "react"
import {Editable, EditableElementProps} from "../HorizontalField/EditableHorizontalField"
import {FontAwesomeIcon} from "@fortawesome/react-fontawesome"
import {faQuestionCircle} from "@fortawesome/free-solid-svg-icons"
import ReactTooltip from "react-tooltip"
import {isSandboxEnv} from "../../utilities/environment"
import {getRoleDisplayName, getAvailableRoles, SPECIAL_ROLES_FOR_ORG_IDS} from "./userRoleMapping"

export interface EditableUserRoleProps {
	orgId?: string
	role: string
	type: string
	setRole: (value: string) => void
	isEditing: boolean
	isDisabled: boolean
	isAllowed: boolean
	onStartEdit: () => void
	onCancel: () => void
	update: (value: string) => void
}

export interface SelectUserRoleProps {
	type: string
	role: string
	setRole: (value: string) => void
	orgId?: string
	partnerId?: string
}

export function UserRoleField({role, type}: {role: string; type: string}) {
	return (
		<HorizontalField label="User Role">
			<input type="text" readOnly value={getRoleDisplayName(type, role)} className="input is-static" />
		</HorizontalField>
	)
}

export function SelectUserRoleField(props: SelectUserRoleProps) {
	const isSandbox = isSandboxEnv()
	const availableRoles = getAvailableRoles(props.type, props.orgId)

	return (
		<div className="select">
			<select value={props.role} onChange={(e) => props.setRole(e.target.value)}>
				{Array.from(availableRoles.keys()).map((r) => {
					const rolesArray = SPECIAL_ROLES_FOR_ORG_IDS[r]
						? SPECIAL_ROLES_FOR_ORG_IDS[r][isSandbox ? "sandbox" : "prod"]
						: undefined
					const showRoleInOptions = (props.orgId && rolesArray && rolesArray.includes(props.orgId)) || !rolesArray
					if (showRoleInOptions) {
						return (
							<option key={r} value={r}>
								{getRoleDisplayName(props.type, r, props.orgId)}
							</option>
						)
					}
				})}
			</select>
		</div>
	)
}

export function EditableUserRoleField(props: EditableUserRoleProps) {
	return (
		<Editable
			label="User Role"
			isEditing={props.isEditing}
			isDisabled={props.isDisabled}
			isAllowed={props.isAllowed}
			onStartEdit={props.onStartEdit}
			onCancel={props.onCancel}
			setValue={props.setRole}
			value={props.role}
			initial={props.role}
			update={props.update}
			type={props.type}
			tooltip={<UserRolesExplanationTooltip type={props.type} orgId={props.orgId} />}
			orgId={props.orgId}
		>
			{EditableUserRole}
		</Editable>
	)
}

function mapUserRoleToOptionDisplayRole(role: string) {
	switch (role) {
		case "bank":
			return "admin"
		case "bank-readonly":
			return "readonly"
		case "bank-operations":
			return "operations"
		case "org-operations":
			return "operations"
		default:
			return role
	}
}

export function EditableUserRole(props: EditableElementProps<string>) {
	if (props.isEditing) {
		const isSandbox = isSandboxEnv()
		const availableRoles = getAvailableRoles(props.type, props.orgId)

		return (
			<div className="select is-small">
				<select value={mapUserRoleToOptionDisplayRole(props.value)} onChange={(e) => props.setValue(e.target.value)}>
					{Array.from(availableRoles.keys()).map((r: string) => {
						const rolesArray = SPECIAL_ROLES_FOR_ORG_IDS[r]
							? SPECIAL_ROLES_FOR_ORG_IDS[r][isSandbox ? "sandbox" : "prod"]
							: undefined
						const showRoleInOptions = (props.orgId && rolesArray && rolesArray.includes(props.orgId)) || !rolesArray
						if (showRoleInOptions) {
							return (
								<option key={r} value={r}>
									{getRoleDisplayName(props.type, r, props.orgId)}
								</option>
							)
						}
					})}
				</select>
			</div>
		)
	} else {
		return (
			<input
				type="text"
				readOnly
				value={getRoleDisplayName(props.type, props.value, props.orgId)}
				className="input is-static"
			/>
		)
	}
}

export function UserRolesExplanationTooltip({type, orgId}: {type: string; orgId?: string}) {
	const availableRoles = getAvailableRoles(type, orgId)
	const info: Array<JSX.Element> = []

	Array.from(availableRoles.entries()).forEach(([roleId, description]) => {
		info.push(
			<div key={roleId}>
				<p>
					<span className="tooltip-info-header">{getRoleDisplayName(type, roleId, orgId)}</span>
					<br />
					<span className="tooltip-info-text">{description}</span>
				</p>
				<br />
			</div>
		)
	})

	return (
		<a data-tip="React-tooltip" data-event="click focus">
			<FontAwesomeIcon size="sm" icon={faQuestionCircle} />
			<ReactTooltip
				place="bottom"
				effect="solid"
				className="tooltip-info"
				globalEventOff="click"
				border={true}
				borderColor="#C4C4C4"
				backgroundColor="#F5F5F5"
			>
				<p className="tooltip-info-title mt-3">About user roles</p>
				<br />
				{info}
			</ReactTooltip>
		</a>
	)
}
